import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Copy, Send, Ban, Lock, PauseCircle, PlayCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { EVENTOS_ACCIONES } from "@/constants/testIds";
import {
  cancelarEvento,
  cerrarEvento,
  checklistPublicacion,
  duplicarEvento,
  ESTADO_EVENTO,
  pausarEvento,
  publicarEvento,
  reanudarEvento,
} from "@/data/adminEventosStore";
import { getAdminSession } from "@/data/adminAuthStore";

/**
 * Contenedor de diálogos para las acciones del listado.
 * Se controla desde el padre mediante `dialog` (tipo + evento).
 * `onDone(nuevoEventoOpcional)` refresca la lista y cierra el diálogo.
 */
export default function EventosAccionesDialogs({ dialog, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);

  const cerrar = () => {
    setMotivo("");
    setLoading(false);
    onClose();
  };

  if (!dialog) return null;
  const { tipo, evento } = dialog;
  const nombre = evento?.nombre || "Evento";

  /**
   * Traduce códigos de error del backend a mensajes específicos.
   * Cualquier `detail` que ya venga en español se muestra tal cual.
   */
  const traducirError = (res, accion) => {
    const err = (res && res.error) || "";
    if (err === "network") return "Sin conexión con el servidor. Intenta de nuevo.";
    if (err === "fecha-futura")
      return "Solo se puede cerrar un evento cuya fecha ya pasó.";
    if (err === "cerrado") return "El evento ya está cerrado y no admite cambios.";
    if (typeof err === "string" && err.trim()) return err;
    return `No se pudo ${accion} el evento.`;
  };

  /* ---- Publicar desde listado ---- */
  const handlePublicar = async () => {
    const check = checklistPublicacion(evento);
    if (!check.listo) {
      toast.error("Faltan datos. Se abrirá el asistente para completarlos.");
      cerrar();
      onDone(evento, { goEdit: true });
      return;
    }
    setLoading(true);
    const res = await publicarEvento({
      id: evento.id,
      sesion: getAdminSession(),
      notificar: false,
    });
    setLoading(false);
    if (!res || res.error) {
      toast.error(traducirError(res, "publicar"));
      cerrar();
      return;
    }
    toast.success("Evento publicado.");
    cerrar();
    onDone(res);
  };

  /* ---- Duplicar ---- */
  const handleDuplicar = async () => {
    setLoading(true);
    const nuevo = await duplicarEvento({ id: evento.id });
    setLoading(false);
    if (!nuevo || nuevo.error) {
      toast.error(traducirError(nuevo, "duplicar"));
      cerrar();
      return;
    }
    toast.success("Evento duplicado. Abriendo para edición…");
    cerrar();
    onDone(nuevo, { goEditNew: nuevo.id });
  };

  /* ---- Cancelar ---- */
  const handleCancelar = async () => {
    if (!motivo.trim()) {
      toast.error("El motivo es obligatorio.");
      return;
    }
    setLoading(true);
    const res = await cancelarEvento({
      id: evento.id,
      motivo,
      sesion: getAdminSession(),
    });
    setLoading(false);
    if (!res || res.error) {
      toast.error(traducirError(res, "cancelar"));
      cerrar();
      return;
    }
    toast.success("Evento cancelado.");
    cerrar();
    onDone(res);
  };

  /* ---- Cerrar ---- */
  const handleCerrar = async () => {
    if (!motivo.trim()) {
      toast.error("El motivo es obligatorio.");
      return;
    }
    setLoading(true);
    const res = await cerrarEvento({
      id: evento.id,
      motivo,
      sesion: getAdminSession(),
    });
    setLoading(false);
    if (!res || res.error) {
      toast.error(traducirError(res, "cerrar"));
      cerrar();
      return;
    }
    toast.success("Evento cerrado.");
    cerrar();
    onDone(res);
  };

  /* ---- Pausar ---- */
  const handlePausar = async () => {
    setLoading(true);
    const res = await pausarEvento({
      id: evento.id,
      sesion: getAdminSession(),
    });
    setLoading(false);
    if (!res || res.error) {
      const status = res?.status;
      if (status === 403) toast.error("No tienes permisos para pausar la venta.");
      else toast.error(traducirError(res, "pausar"));
      cerrar();
      return;
    }
    toast.success("Venta pausada.");
    cerrar();
    onDone(res);
  };

  /* ---- Reanudar ---- */
  const handleReanudar = async () => {
    setLoading(true);
    const res = await reanudarEvento({
      id: evento.id,
      sesion: getAdminSession(),
    });
    setLoading(false);
    if (!res || res.error) {
      const status = res?.status;
      if (status === 403) toast.error("No tienes permisos para reanudar la venta.");
      else toast.error(traducirError(res, "reanudar"));
      cerrar();
      return;
    }
    toast.success("Venta reanudada.");
    cerrar();
    onDone(res);
  };

  const open = !!dialog;

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? cerrar() : null)}>
      <DialogContent
        data-testid={
          tipo === "publicar"
            ? EVENTOS_ACCIONES.publicarDialog
            : tipo === "duplicar"
              ? EVENTOS_ACCIONES.duplicarDialog
              : tipo === "cancelar"
                ? EVENTOS_ACCIONES.cancelarDialog
                : tipo === "pausar"
                  ? EVENTOS_ACCIONES.pausarDialog
                  : tipo === "reanudar"
                    ? EVENTOS_ACCIONES.reanudarDialog
                    : EVENTOS_ACCIONES.cerrarDialog
        }
      >
        {tipo === "publicar" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-4 h-4 text-accent" /> Publicar evento
              </DialogTitle>
              <DialogDescription>
                ¿Desea publicar “{nombre}”? La información será visible para los
                socios.
              </DialogDescription>
            </DialogHeader>
            {evento.estado !== ESTADO_EVENTO.BORRADOR && (
              <div className="rounded-lg border hairline bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2 text-xs flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5" />
                Publicar sólo se permite desde el estado Borrador.
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={cerrar}>Cancelar</Button>
              <Button
                data-testid={EVENTOS_ACCIONES.publicarConfirm}
                disabled={
                  loading || evento.estado !== ESTADO_EVENTO.BORRADOR
                }
                onClick={handlePublicar}
                className="bg-primary text-primary-foreground"
              >
                Confirmar publicación
              </Button>
            </DialogFooter>
          </>
        )}

        {tipo === "duplicar" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="w-4 h-4 text-accent" /> Duplicar evento
              </DialogTitle>
              <DialogDescription>
                Se creará un nuevo evento como <b>Borrador</b> con los datos de
                “{nombre}”. No se copiarán compras, boletos ni historial.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cerrar}>Cancelar</Button>
              <Button
                data-testid={EVENTOS_ACCIONES.duplicarConfirm}
                disabled={loading}
                onClick={handleDuplicar}
                className="bg-primary text-primary-foreground"
              >
                Duplicar y abrir
              </Button>
            </DialogFooter>
          </>
        )}

        {tipo === "cancelar" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-destructive" /> Cancelar evento
              </DialogTitle>
              <DialogDescription>
                Escribe el motivo de la cancelación. La información se
                preservará en el histórico.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label>Motivo *</Label>
              <Textarea
                data-testid={EVENTOS_ACCIONES.cancelarMotivo}
                rows={3}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Explica brevemente el motivo…"
                className="resize-none"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={cerrar}>Cancelar</Button>
              <Button
                data-testid={EVENTOS_ACCIONES.cancelarConfirm}
                disabled={loading || !motivo.trim()}
                onClick={handleCancelar}
                className="bg-destructive text-destructive-foreground"
              >
                Confirmar cancelación
              </Button>
            </DialogFooter>
          </>
        )}

        {tipo === "cerrar" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" /> Cerrar evento
              </DialogTitle>
              <DialogDescription>
                Cerrar un evento lo bloquea por completo y no permite más
                modificaciones. Solo procede cuando la fecha ya haya pasado.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label>Motivo *</Label>
              <Textarea
                data-testid={EVENTOS_ACCIONES.cerrarMotivo}
                rows={3}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Explica brevemente el cierre…"
                className="resize-none"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={cerrar}>Cancelar</Button>
              <Button
                data-testid={EVENTOS_ACCIONES.cerrarConfirm}
                disabled={loading || !motivo.trim()}
                onClick={handleCerrar}
                className="bg-primary text-primary-foreground"
              >
                Confirmar cierre
              </Button>
            </DialogFooter>
          </>
        )}
        {tipo === "pausar" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PauseCircle className="w-4 h-4 text-amber-500" /> Pausar venta
              </DialogTitle>
              <DialogDescription>
                La venta de “{nombre}” quedará pausada. Los socios que ya tienen
                turno en Smart Queue conservarán su turno y tiempo restante.
                Las compras y boletos existentes NO se afectan. Nuevos socios
                no podrán entrar a la fila hasta que reanudes la venta.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cerrar}>Cancelar</Button>
              <Button
                data-testid={EVENTOS_ACCIONES.pausarConfirm}
                disabled={loading}
                onClick={handlePausar}
                className="bg-amber-500 text-white hover:bg-amber-600"
              >
                Confirmar pausa
              </Button>
            </DialogFooter>
          </>
        )}

        {tipo === "reanudar" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4 text-emerald-500" /> Reanudar venta
              </DialogTitle>
              <DialogDescription>
                La venta de “{nombre}” volverá a estar activa. Los socios podrán
                unirse a la Smart Queue nuevamente. La fila existente se
                conserva sin duplicar turnos ni posiciones.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={cerrar}>Cancelar</Button>
              <Button
                data-testid={EVENTOS_ACCIONES.reanudarConfirm}
                disabled={loading}
                onClick={handleReanudar}
                className="bg-primary text-primary-foreground"
              >
                Confirmar reanudación
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
